import { Log } from "@accurx/shared";
import { isNil } from "es-toolkit/compat";

import {
    AssigneeType,
    PatientThreadAssignee,
    PatientThreadContentType,
    PatientThreadTicketDetails,
    PatientThreadTicketItem,
    TicketIdentity,
    TicketIdentityType,
    TicketSummary,
} from "shared/concierge/conversations/tickets/types/dto.types";
import { AssigneeSummary } from "shared/concierge/conversations/types/assignee.types";
import type {
    ConversationSummary,
    ConversationUpdate,
    DeprecatedConversationStatus,
} from "shared/concierge/conversations/types/conversation.types";
import type { ConversationItem } from "shared/concierge/conversations/types/item.types";

import { mapTicketItemToConversationItem } from "./ConversationItemMapper";

/**
 * Maps from an external "Ticket" DTO to an internal domain view - A "Conversation"
 * @param ticket - An external "Ticket" DTO
 * @returns A conversation
 */
export const mapTicketToConversation = (
    ticket: PatientThreadTicketDetails,
): ConversationUpdate | undefined => {
    const ticketSummary = ticket.ticketSummary;
    const ticketIdentity = ticketSummary?.identity;

    if (
        isNil(ticketSummary) ||
        isNil(ticketIdentity) ||
        isNil(ticketIdentity.id)
    ) {
        Log.error("Missing conversation identity");
        return undefined;
    }

    const conversationId = createUniqueConversationId(ticketIdentity);

    if (isNil(ticketSummary.lastUpdated) || isNil(ticketSummary.latestToken)) {
        Log.error("Missing last update information", {
            tags: { conversationId },
        });
        return undefined;
    }

    const status = mapConversationState(ticketSummary);
    const assignee = mapAssignee(ticketSummary.assignee, status);

    // FOU-149: deprecating ticket.patient over ticketSummary.patientId
    const patientId =
        ticketSummary.patientId ?? ticket.patient?.accuRxId ?? null;

    const initialSummary: ConversationSummary = {
        id: conversationId,
        status: status,
        assignee: assignee,
        isUrgent: ticketSummary.isUrgent ?? false,
        lastUpdated: ticketSummary.lastUpdated,
        latestToken: ticketSummary.latestToken,
        regardingPatientId: patientId,
        // not currently tracking whether a conversation is fully loaded or not
    };

    // Items within a conversation.
    const items = mapConversationItems(ticket);

    // Ignore conversations we can't show any data for.
    if (items.length === 0) {
        Log.error("Missing conversation items", { tags: { conversationId } });
        return undefined;
    }

    return {
        ...initialSummary,
        items,
        isFullyLoaded: !!ticket.isFullyLoaded,
    };
};

/**
 * Currently ticket items of different types can have clashing IDs.
 * This method builds an ID that's guarenteed to be unique by namespacing
 * the ID with the item type.
 *
 * @param patientThreadContentType
 * @param patientThreadItemId
 * @returns
 */
export const createUniqueNoteId = (
    patientThreadContentType: PatientThreadContentType,
    patientThreadItemId: NonNullable<PatientThreadTicketItem["id"]>,
): string => {
    return `t:${patientThreadContentType}-i:${patientThreadItemId}`;
};

export const createUniqueConversationId = (
    identity: TicketIdentity,
): string => {
    return `t:${identity.type}-id:${identity.id}`;
};

const uniqueIdRegex = /^t:(\d+)-(\w+):(\w+)$/;
const parseUniqueId = (
    id: string | undefined | null,
    expectedValueKey: string,
) => {
    const match = id?.match(uniqueIdRegex);
    // explicit on prefix string for the id value, as items and conversations have a similar scheme
    return match && match[2] === expectedValueKey
        ? {
              type: match[1],
              value: match[3],
          }
        : undefined;
};

/**
 * Provides the original server ID for a mapped item, used for markAsRead etc.
 */
export const parseUniqueNoteId = (
    conversationItemId: string,
): string | undefined => {
    const parsed = parseUniqueId(conversationItemId, "i");
    if (parsed) {
        return parsed.value;
    }
    Log.error("Unable to parse unique note ID", {
        tags: { conversationItemId },
    });
    return undefined;
};

type RequiredTicketIdentity = {
    type: TicketIdentityType;
    id: string;
};

/**
 * Provides the original server identity for a mapped conversation,
 * used for assign etc.
 */
export const parseUniqueConversationId = (
    conversationId: string,
): RequiredTicketIdentity | undefined => {
    const parsed = parseUniqueId(conversationId, "id");
    if (parsed) {
        return {
            type: parseInt(parsed.type, 10),
            id: parsed.value,
        };
    }
    Log.error("Unable to parse unique conversation ID", {
        tags: { conversationId },
    });
    return undefined;
};

export const getPatientThreadAssignee = (
    assignee: AssigneeSummary,
): PatientThreadAssignee | null => {
    if (assignee.type === "User" && assignee.id) {
        return {
            type: AssigneeType.User,
            userId: assignee.id,
        };
    }
    if (assignee.type === "Team" && assignee.id) {
        return {
            type: AssigneeType.UserGroup,
            userGroupId: assignee.id,
        };
    }
    return null;
};

function mapConversationItems(
    ticket: PatientThreadTicketDetails,
): ConversationItem[] {
    const mappedConversationItems = new Array<ConversationItem>();
    if (ticket.items) {
        ticket.items.forEach((ticketItem) => {
            const mappedItem = mapTicketItemToConversationItem(ticketItem);

            if (mappedItem) {
                mappedConversationItems.push(mappedItem);
            }
        });
    }
    return mappedConversationItems;
}

function mapAssignee(
    dto: PatientThreadAssignee | null | undefined,
    status: DeprecatedConversationStatus,
): AssigneeSummary {
    if (isNil(dto) || (dto.type === AssigneeType.None && status === "None")) {
        // currently still supported for pseudo tickets
        return { type: "None" };
    }

    if (dto.type === AssigneeType.User && !isNil(dto.userId)) {
        return {
            type: "User",
            id: dto.userId,
        };
    }

    if (dto.type === AssigneeType.UserGroup && !isNil(dto.userGroupId)) {
        return {
            type: "Team",
            id: dto.userGroupId,
        };
    }

    return { type: "None" };
}

function mapConversationState(
    ticket: TicketSummary,
): DeprecatedConversationStatus {
    switch (ticket.isDone) {
        case true:
            return "Done";
        case false:
            return "Open";
        default:
            // The isDone property passes a null value when the backend returns a pseudo-ticket e.g. representing a single SMS
            return "None";
    }
}
